import { getProjectCommentUrl } from 'base/blocks_routes';
import { exists, relativeTime } from 'base/helpers';
import { Button } from 'base/ui/button';
import { Dialog } from 'base/ui/dialog';
import { formatError, RedErrorText, useSubmit } from 'base/ui/form';
import { IconCheck, IconCheckCircle, IconOption } from 'base/ui/icons';
import { Rows } from 'base/ui/layout';
import { MenuButton } from 'base/ui/menu';
import { menuItem } from 'base/ui/menu_model';
import { ToastQueue } from 'base/ui/toast';
import { TextSmallLight, TextTiny } from 'base/ui/typography';
import { Comment, CommentThread, UserAvatar } from 'blocks/blocks_service';
import { observer } from 'mobx-react-lite';
import React from 'react';
import { CommentingUiStore } from './ui_store';

export function uniqueAvatars(comments: readonly Comment[]) {
  const seen = new Set<string>();
  const out: UserAvatar[] = [];
  for (const c of comments) {
    const key = `${c.author.firstName}|${c.author.lastName}|${c.author.color}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(c.author);
  }
  return out;
}

export function CommentContent({
  comment,
  faded,
  contentLineClamp,
}: {
  comment: Pick<Comment, 'author' | 'createdAt' | 'content'>;
  faded?: boolean;
  contentLineClamp?: number;
}) {
  return (
    <Rows spacing={2}>
      <TextTiny color="grey">
        <TextTiny tag="span" color={faded ? 'grey' : 'black'}>
          {comment.author.firstName} {comment.author.lastName}
        </TextTiny>
        {' • '}
        {relativeTime(+new Date(comment.createdAt) / 1000)}
      </TextTiny>
      <TextSmallLight
        preWrap
        selectable
        wordBreak="break-word"
        lineClamp={contentLineClamp}
        color={faded ? 'grey' : 'black'}
      >
        {comment.content}
      </TextSmallLight>
    </Rows>
  );
}

export function ThreadResolveButton({
  thread,
  store,
  toastQueue,
}: {
  thread: CommentThread;
  store: CommentingUiStore;
  toastQueue: ToastQueue;
}) {
  const [loading, _, handleSubmit] = useSubmit(async () => {
    const resolved = !thread.resolved;
    try {
      if (resolved && store.selectedThreadId === thread.id) {
        store.selectedThreadId = undefined;
      }
      await store.comments.updateThread(thread.id, { resolved });
      store.session.analytics.track({
        event: resolved
          ? 'project:comment_resolve'
          : 'project:comment_unresolve',
        comment_thread_id: thread.id,
      });
    } catch (e) {
      toastQueue.push({
        label: `Error ${
          thread.resolved ? 're-opening' : 'resolving'
        } thread: ${formatError(e)}.`,
      });
    }
  });

  if (!store.canComment && !thread.resolved) return null;
  return (
    <Button
      icon={thread.resolved ? IconCheckCircle : IconCheck}
      iconColor={thread.resolved ? 'green' : undefined}
      disabled={loading || !store.loggedIn || !store.canComment}
      tooltip={
        loading
          ? 'Saving...'
          : store.canComment
          ? thread.resolved
            ? store.loggedIn
              ? 'Re-open'
              : 'Log in to re-open this thread.'
            : store.loggedIn
            ? 'Resolve'
            : 'Log in to resolve this thread.'
          : 'You do not have permission to resolve comments.'
      }
      variant="tertiary"
      size="small"
      onClick={handleSubmit}
    />
  );
}

export const ThreadMenuButton = observer(function _ThreadMenuButton({
  thread,
  store,
  toastQueue,
}: {
  thread: CommentThread;
  store: CommentingUiStore;
  toastQueue: ToastQueue;
}) {
  const [deleteMenuItem, deleteDialog] = useDeleteThreadMenu(thread.id, store);
  const isRead = store.comments.isThreadRead(thread.id);

  const onClickMarkAsUnread = React.useCallback(async () => {
    const comments = store.comments.getCommentsInThread(thread.id);
    const lastComment = comments[comments.length - 1];
    if (!lastComment) return;
    if (store.selectedThreadId === thread.id) {
      // Hide it before marking it as unread, so it doesn't become
      // read again immediately.
      store.selectedThreadId = undefined;
    }
    try {
      await store.comments.setCommentRead(lastComment.id, false);
    } catch (e) {
      toastQueue.push({
        label: `Error marking as unread: ${formatError(e)}.`,
      });
    }
  }, [thread.id, store, toastQueue]);

  const onClickCopyLink = React.useCallback(
    () => {
      const url = getProjectCommentUrl(store.session.projectToken, thread.id);
      navigator.clipboard.writeText(url);
      toastQueue.push({ label: 'Copied to clipboard' });
    }, // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [thread.id, toastQueue],
  );

  return (
    <>
      <MenuButton
        menu={[
          isRead && store.loggedIn
            ? menuItem({
                label: 'Mark as unread',
                onClick: onClickMarkAsUnread,
              })
            : undefined,
          menuItem({ label: 'Copy link', onClick: onClickCopyLink }),
          deleteMenuItem,
        ].filter(exists)}
        search="hidden"
        icon={IconOption}
        variant="tertiary"
        size="small"
      />
      {deleteDialog}
    </>
  );
});

function useDeleteThreadMenu(threadId: number, store: CommentingUiStore) {
  const [isConfirming, setIsConfirming] = React.useState(false);

  const canDelete = store.comments.canDeleteThread(threadId);

  const [loading, error, handleSubmit] = useSubmit(async () => {
    await store.comments.updateThread(threadId, { deleted: true });
    if (store.selectedThreadId === threadId) {
      store.selectedThreadId = undefined;
    }
  });

  const dialog = (
    <Dialog
      title="Delete this comment?"
      onClose={() => setIsConfirming(false)}
      buttons={
        <>
          <Button
            label="Cancel"
            onClick={() => setIsConfirming(false)}
            size="large"
          />
          <Button
            label="Delete"
            onClick={handleSubmit}
            loading={loading}
            variant="primary"
            background="negative"
            size="large"
          />
        </>
      }
    >
      <TextSmallLight color="grey">
        Are you sure you want to delete this comment thread? This will delete
        any replies as well.
      </TextSmallLight>
      <RedErrorText error={error} />
    </Dialog>
  );

  return [
    canDelete
      ? menuItem({
          label: 'Delete thread',
          onClick: () => setIsConfirming(true),
        })
      : undefined,
    isConfirming ? dialog : undefined,
  ] as const;
}
